import dayjs from 'dayjs';
import React from 'react';
import s from './s.module.less';
import { Event, EventWithTimeOverlap } from 'types/calendar';
import { getEventColorSetting, isCancelledEvent, isEventTimeOverlap } from 'utils/calendar';
import useCalendarColors from 'hooks/useCalendarColors';
import AddAndEditCalendarEvent from 'components/AddAndEditCalendarEvent';
import { Popover } from 'antd';
import { KlarityService, Prospect } from 'types/common';
import EventTitle from '../EventTitle';
import TaskPills, { isTaskLikeEvent } from '../TaskPills';

type Props = {
    useAdminApi?: boolean;
    day: dayjs.Dayjs;
    events: Event[];
    onEventEdit?: (event: Event) => void;
    timezone?: string;
    onEventAdd?: (start: dayjs.Dayjs, end: dayjs.Dayjs) => void;
    pendingStartTimeForDay?: dayjs.Dayjs;
    pendingEndTimeForDay?: dayjs.Dayjs;
    onHidePopLayerForDay?: () => void;
    onEventUpdate?: () => void;
    usePopLayer?: boolean;
    allSpecialityData?: KlarityService[];
    allClients?: Prospect[];
    providerColorMap?: Record<number, string>;
    onEditRefillTask?: (item: any) => void;
};

const Day = (props: Props) => {
    const {
        day,
        events,
        onEventEdit,
        useAdminApi,
        timezone,
        onEventAdd,
        pendingEndTimeForDay,
        pendingStartTimeForDay,
        onHidePopLayerForDay,
        onEventUpdate,
        usePopLayer = true,
        allSpecialityData,
        allClients,
        providerColorMap,
        onEditRefillTask,
    } = props;
    useCalendarColors({ useAdminApi });
    const [currentEvent, setCurrentEvent] = React.useState<Event>();
    const [showEdit, setShowEdit] = React.useState(false);

    const formatHourLabel = (hour24: number) => {
        const h = ((hour24 % 24) + 24) % 24;
        const isAm = h < 12;
        if (h === 0) return '12 am';
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        return `${hour12} ${isAm ? 'am' : 'pm'}`;
    };

    const { startHour: viewStartHour, endHour } = React.useMemo(() => {
        let latest: number | undefined;

        events.forEach((evt) => {
            const summary = (evt.summary || '').trim();
            const isTaskLike = evt.type === 'task' || summary.startsWith('📃') || summary.startsWith('✏️');
            if (isTaskLike) return;
            let endAt = dayjs(evt.end?.dateTime);
            if (timezone) {
                endAt = endAt.tz(timezone);
            }
            latest = latest === undefined ? endAt.hour() : Math.max(latest, endAt.hour());
        });

        const start = 0;
        const endByLatest = latest === undefined ? 24 : Math.max(24, latest + 1);
        return { startHour: start, endHour: endByLatest };
    }, [events, timezone]);

    const timeArray = React.useMemo(() => {
        const hours: number[] = [];
        for (let h = viewStartHour; h <= endHour; h++) hours.push(h);
        return hours.map(formatHourLabel);
    }, [viewStartHour, endHour]);

    const gridHeight = React.useMemo(() => {
        const rows = Math.max(1, endHour - viewStartHour + 1);
        return rows * 50 + 120;
    }, [endHour, viewStartHour]);
    const isCurrentDay = day.format('DD-MM-YY') === dayjs().format('DD-MM-YY');
    const eventsWithTimeOverlap: EventWithTimeOverlap[] = React.useMemo(() => {
        const ret: EventWithTimeOverlap[] = [];
        for (let i = 0; i < events.length; i++) {
            const evt = events[i];
            const timeOverlapKeys: string[] = [];
            for (let j = 0; j < i; j++) {
                const event2 = events[j];
                if (isEventTimeOverlap(evt, event2)) {
                    timeOverlapKeys.push(event2.id);
                }
            }
            ret.push({ ...events[i], timeOverlapKeys });
        }
        return ret;
    }, [events]);

    const taskEvents = React.useMemo(() => {
        return (eventsWithTimeOverlap || []).filter(isTaskLikeEvent);
    }, [eventsWithTimeOverlap]);

    const timedEvents = React.useMemo(() => {
        return (eventsWithTimeOverlap || []).filter((e) => !isTaskLikeEvent(e));
    }, [eventsWithTimeOverlap]);

    const showAddPendingArea = !!pendingStartTimeForDay;
    let pendingTop = 0;
    let pendingHeight = 0;
    if (showAddPendingArea) {
        const pendingStartHour = pendingStartTimeForDay.hour();
        const startMin = pendingStartTimeForDay.minute();
        pendingTop = (pendingStartHour - viewStartHour) * 50 + (startMin * 5) / 6;
        pendingHeight = 25;
    }

    const pendingAreaContent = () => {
        return (
            <div
                className={s.areaWrap}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <AddAndEditCalendarEvent
                    onCancel={() => {
                        onHidePopLayerForDay?.();
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                    }}
                    onSuccess={() => {
                        onEventUpdate?.();
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                    }}
                    defaultStart={pendingStartTimeForDay}
                    defaultEnd={pendingEndTimeForDay}
                    timezone={timezone}
                    isPopLayer
                    event={currentEvent}
                    allSpecialityData={allSpecialityData}
                />
                <div
                    onClick={() => {
                        setCurrentEvent(undefined);
                        setShowEdit(false);
                        onHidePopLayerForDay?.();
                    }}
                    className={s.closeIcon}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M6 18L18 6M6 6L18 18"
                            stroke="#9CA3AF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>
        );
    };

    return (
        <div className={s.wrap}>
            <div className={s.itemHeader}>
                <div className={s.header}>{day.format('ddd').toUpperCase()}</div>
                <div>
                    <span className={isCurrentDay ? `${s.dayLabel} ${s.today}` : s.dayLabel}>{day.format('DD')}</span>
                </div>
                {taskEvents.length > 0 && (
                    <div className={s.headerTasks} onClick={(e) => e.stopPropagation()}>
                        <TaskPills
                            events={taskEvents}
                            onEventUpdate={onEventUpdate}
                            onEdit={onEditRefillTask}
                            placement="right"
                            pillClassName={s.taskPill}
                            getPopupContainer={() => document.body}
                            as="button"
                            onBeforeOpen={() => {
                                // Keep behavior consistent with week/day: avoid keeping add/edit poplayer open underneath.
                                onHidePopLayerForDay?.();
                                setCurrentEvent(undefined);
                                setShowEdit(false);
                            }}
                        />
                    </div>
                )}
            </div>
            <div className={s.content}>
                <div className={s.timeWrap}>
                    <div className={s.dayBg}>
                        {timeArray.map((item) => {
                            return (
                                <div className={`${s.rowLine} rowLine`} key={item}>
                                    <span className={s.label}>{item}</span>
                                    <hr className={s.line} />
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div
                    className={s.eventWrap}
                    style={{ height: gridHeight }}
                    onClick={(e) => {
                        const offsetTop = e.nativeEvent.offsetY;
                        const targetHour = viewStartHour + Math.floor(offsetTop / 50);
                        const left = offsetTop % 50;
                        let min = 0;
                        if (left > 12.5 && left <= 25) {
                            min = 15;
                        }
                        if (left > 25 && left <= 37.5) {
                            min = 30;
                        }
                        if (left > 37.5 && left <= 50) {
                            min = 45;
                        }
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const startTime = day.hour(targetHour).minute(min);
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        let endTime = day.hour(targetHour).minute(min).add(30, 'minute');
                        if (endTime.isAfter(dayjs(day).endOf('day'))) {
                            endTime = dayjs(day).endOf('day');
                        }
                        setCurrentEvent(undefined);
                        if (onEventAdd) onEventAdd(startTime, endTime);
                    }}
                >
                    {timedEvents.map((evt, idx) => {
                        let startTimeDayjs = dayjs(evt.start.dateTime);
                        let endTimeDayjs = dayjs(evt.end.dateTime);
                        if (timezone) {
                            startTimeDayjs = startTimeDayjs.tz(timezone);
                            endTimeDayjs = endTimeDayjs.tz(timezone);
                        }

                        const timeLabel = `${startTimeDayjs.format('hh:mm')} - ${endTimeDayjs.format('hh:mm A')}`;

                        // Compute position based on real timestamps, clamped to the visible day range.
                        // This prevents cross-day / malformed payloads from rendering as a giant full-day block.
                        const viewStart = dayjs(day).hour(viewStartHour).minute(0).second(0).millisecond(0);
                        const viewEnd = dayjs(day).endOf('day');
                        const clampStart = startTimeDayjs.isBefore(viewStart) ? viewStart : startTimeDayjs;
                        const clampEnd = endTimeDayjs.isAfter(viewEnd) ? viewEnd : endTimeDayjs;

                        const topMin = Math.max(0, clampStart.diff(viewStart, 'minute'));
                        const heightMin = Math.max(0, clampEnd.diff(clampStart, 'minute'));

                        const top = (topMin * 50) / 60;
                        const heightPx = heightMin > 0 ? (heightMin * 50) / 60 : 20;
                        const cssColor = getEventColorSetting(evt, allClients, providerColorMap);

                        const overlapCount = evt.timeOverlapKeys.length;
                        const isBlock = evt.type === 'blocker';
                        const eventHeight = heightPx;
                        let narrowLayout = false;

                        if (eventHeight > 0 && eventHeight < 15) {
                            narrowLayout = true;
                        }

                        let smallLine = false;
                        if (eventHeight > 25 && eventHeight < 40) {
                            smallLine = true;
                        }

                        let className = `${s.event} ${isBlock ? s.eventBlocker : ''}`;
                        if (isCancelledEvent(evt)) {
                            className = `${className} ${s.eventCancelled}`;
                        }
                        if (smallLine) {
                            className = `${className} ${s.eventSmallLine}`;
                        }

                        if (narrowLayout) {
                            className = `${className} ${s.narrowEvent}`;
                        }
                        className = `${className} weekEventItem`;
                        const isEditing = currentEvent && currentEvent.id === evt.id && showEdit;
                        if (isEditing) {
                            return (
                                <Popover
                                    key={evt.id}
                                    open
                                    zIndex={1000}
                                    placement="bottom"
                                    title={null}
                                    content={pendingAreaContent()}
                                    arrow
                                >
                                    <div
                                        className={className}
                                        style={{
                                            top,
                                            height: `${eventHeight}px`,
                                            right: 0,
                                            left: overlapCount * 20,
                                            color: cssColor.color,
                                            backgroundColor: cssColor.backgroundColor,
                                            border: `0.5px ${cssColor.borderStyle} ${cssColor.borderColor}`,
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (usePopLayer) {
                                                onHidePopLayerForDay?.();
                                                setCurrentEvent(evt);
                                                setShowEdit(true);
                                            } else if (onEventEdit) {
                                                onEventEdit(evt);
                                            }
                                        }}
                                    >
                                        <p className={s.title}>
                                            <EventTitle event={evt} allSpecialityData={allSpecialityData} />
                                            {/* {getEventTitleDisplay(evt) || 'No title'} */}
                                        </p>
                                        <p className={s.time}>{timeLabel}</p>
                                    </div>
                                </Popover>
                            );
                        }
                        return (
                            <div
                                key={idx}
                                className={className}
                                style={{
                                    top,
                                    height: `${eventHeight}px`,
                                    right: 0,
                                    left: overlapCount * 20,
                                    color: cssColor.color,
                                    backgroundColor: cssColor.backgroundColor,
                                    border: `0.5px ${cssColor.borderStyle} ${cssColor.borderColor}`,
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (usePopLayer) {
                                        onHidePopLayerForDay?.();
                                        setCurrentEvent(evt);
                                        setShowEdit(true);
                                    } else if (onEventEdit) {
                                        onEventEdit(evt);
                                    }
                                }}
                            >
                                <p className={s.title}>
                                    <EventTitle event={evt} allSpecialityData={allSpecialityData} />
                                    {/* {getEventTitleDisplay(evt) || 'No title'} */}
                                </p>
                                <p className={s.time}>{timeLabel}</p>
                            </div>
                        );
                    })}
                    {showAddPendingArea && (
                        <Popover
                            open
                            placement="bottom"
                            title={null}
                            zIndex={1000}
                            content={pendingAreaContent()}
                            arrow
                        >
                            <div
                                className={s.event}
                                style={{
                                    top: pendingTop,
                                    height: `${pendingHeight}px`,
                                    right: 24,
                                    left: 0,
                                }}
                                onClick={() => {}}
                            >
                                <p className={s.title}>{/* No title */}</p>
                            </div>
                        </Popover>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Day;
